import { Injectable } from '@angular/core';
import { ParcelModel } from '@app/common/models/parcel.model';
import { OccupationModel } from '../models/occupation.model';
import * as _ from 'lodash';
import { WordService } from '@app/common/services/word.service';

export type OccupationGroupsType = {
  permanent: OccupationGroupedType[],
  overOneYear: OccupationGroupedType[],
  underOneYear: OccupationGroupedType[]
};

export type OccupationGroupedType = {
    id: string,
    key: string,
    parcel: ParcelModel,
    occupations: OccupationModel[],
};

@Injectable({
  providedIn: 'root'
})
export class ParcelService {

  static readonly PERMANENT_OCCUPATION_TYPES = [1, 2, 3, 4, 7, 8, 11, 12, 41, 42, 111, 112, 113, 121, 122];

  constructor(
    private wordService: WordService,
  ) {
    this.groupOccupationsByParcel = this.groupOccupationsByParcel.bind(this);
    this.groupOccupationsByParcelSingle = this.groupOccupationsByParcelSingle.bind(this);
    this.getTemporaryOccupationName = this.getTemporaryOccupationName.bind(this);
    this._groupOccupationsByParcel = this._groupOccupationsByParcel.bind(this);
  }

  groupOccupationsByParcel(occupations: OccupationModel[], parcelsData: ParcelModel[] = null): OccupationGroupsType {
    return this._groupOccupationsByParcel(occupations, parcelsData, false) as OccupationGroupsType;
  }

  groupOccupationsByParcelSingle(occupations: OccupationModel[], parcelsData: ParcelModel[] = null): OccupationGroupedType[] {
    return this._groupOccupationsByParcel(occupations, parcelsData, true) as OccupationGroupedType[];
  }

  getTemporaryOccupationName(group: OccupationGroupedType[]): string {
    if (!group || !group[0] || !group[0].occupations || !group[0].occupations[0]) {
      return '';
    }

    return group[0].occupations[0].zabtyptx
      .replace(/dočasný zábor/i, this.wordService.getTranslation('TEMPORARY_OCCUPATION'));
  }

  public checkAreaSuitableForPricing(parcel: ParcelModel, occupationType: string) {
    const check = {
      disabled: false,
      error: ''
    };

    if (occupationType !== 'P') {
      return check;
    }

    const vymeraZab = parcel.occupations
      .filter((o) => !!!o.endDate)
      .filter((o) => ParcelService.PERMANENT_OCCUPATION_TYPES.includes(o.occupationType.number))
      .reduce((sum, item) => sum + (item.zabvym === undefined ? 0 : item.zabvym), 0);

    const vymeraOk = parcel.vymera === vymeraZab;

    if (!vymeraOk) {
      check.disabled = true;
      check.error = `Výměra záboru na parcele (${vymeraZab} m2) se neshoduje s výměrou parcely! Nelze zadat cenu.`;
      return check;
    }

    const occTypes = parcel.occupations
      .filter(o => !!!o.endDate && [3, 7, 12, 121, 122, 41].includes(o.occupationType.number))
      .map(o => o.occupationType.name);

    if (occTypes.length > 0) {
      check.error = `Doporučujeme kontrolu. Na parcele se nachází typ trvalého záboru (${occTypes.join(', ')}), který není určen k výkupu.`;
    }

    return check;
  }

  public checkAreaSuitableForCase(parcel: ParcelModel, occupations: OccupationModel[], occupationType: string) {
    const check = {
      disabled: false,
      error: ''
    };

    if (occupationType !== 'permanent') {
      return check;
    }

    const vymeraZab = occupations
      .filter((o) => !!!o.endDate)
      .filter((o) => ParcelService.PERMANENT_OCCUPATION_TYPES.includes(o.occupationType.number))
      .reduce((sum, item) => sum + (item.zabvym === undefined ? 0 : item.zabvym), 0);

    const vymeraOk = parcel.vymera === vymeraZab;

    if (!vymeraOk) {
      check.disabled = true;
      check.error = `Výměra záboru na parcele (${vymeraZab} m2) se neshoduje s výměrou parcely! Parcelu nelze vložit do vybraného typu případu.`;
    }

    return check;
  }

  /**
   * Určí skupinu záborů podle čísla typu záboru a seskupí záznamy. Trvalé zábory jsou navíc seskupeny podle parcely.
   * Pokud je určen způsob řešení trvalého záboru nájmem je skupina určena tímto. Pro specifické účely jsou tyto děleny do zvláštních skupin a zároveň jedné sloučené.
   * @param {type} data
   * @param {type} parcelsData
   * @param {type} singleLevel
   * @returns {Array}
   */
  private _groupOccupationsByParcel(data, parcelsData, singleLevel) {
    singleLevel = singleLevel === true;
    const dataOccupationGroups = singleLevel ? [] : {};
    data.forEach((occupation) => {
      const groupKey = occupation.solutionType || occupation.occupationType.category;

      let occupationGrouped;

      if (!singleLevel && !dataOccupationGroups[groupKey]) {
        dataOccupationGroups[groupKey] = [];
      }
      if (groupKey === 'permanent') {
        const itemFound = _.find(singleLevel ? dataOccupationGroups : dataOccupationGroups[groupKey], (group) => {
          return (!singleLevel || group.key === groupKey) && occupation.parcel && group.parcel.id === occupation.parcel.id;
        });
        if (itemFound) {
          itemFound.occupations.push(occupation);
          return;
        } else if (occupation.parcel) {
          if (parcelsData instanceof Array) {
            parcelsData.push(occupation.parcel);
          }
          occupationGrouped = {
            id: groupKey + '_' + occupation.parcel.id,
            key: groupKey,
            parcel: occupation.parcel,
            occupations: [occupation],
          };
        } else {
          // without parcel
          return;
        }
      } else {
        if (parcelsData instanceof Array) {
          parcelsData.push(occupation.parcel);
        }
        occupationGrouped = {
          id: groupKey + '_' + occupation.id,
          key: groupKey,
          parcel: occupation.parcel,
          occupations: [occupation], // there will be only one item
        };
      }
      (singleLevel ? dataOccupationGroups : dataOccupationGroups[groupKey]).push(occupationGrouped);

      if (!singleLevel && groupKey.startsWith('permanentWithSolutionType')) {
        dataOccupationGroups['permanentWithSolutionType'] = dataOccupationGroups['permanentWithSolutionType'] || [];
        dataOccupationGroups['permanentWithSolutionType'].push(occupationGrouped);
      }
    });

    return dataOccupationGroups;
  }
}
